import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { ApiStudyareaService } from '../../services/api-studyarea.service';
import { MessageService } from 'primeng/api';
import { AppDatePipe } from '../../pipes/app-date-pipe';

@Component({
  selector: 'app-studyarea-renew',
  templateUrl: './studyarea-renew.component.html',
  styleUrl: './studyarea-renew.component.scss',
  standalone: false
})

export class StudyareaRenewComponent implements OnInit {
  form: FormGroup;
  model: any = {};
  options: FormlyFormOptions = {};
  renewStudyAreaFields: FormlyFieldConfig[] = [];
  renewStudyArea: any = {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly appDatePipe: AppDatePipe,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private studyareaService: ApiStudyareaService,
    private messageService: MessageService) {
    this.form = this.fb.group({});

  }

  ngOnInit(): void {
    this.initializeFields();
    this.model = {
      projectCode: this.config.data?.studyAreaData?.projectCode,
      taskCode: this.config.data?.studyAreaData?.taskCode,
      timestamp: new Date(this.config.data?.studyAreaData?.timestamp),
      invoiceId: this.config.data?.studyAreaData?.invoiceId,
      content: this.config.data?.content
    }
  }

  initializeFields(): void {
    this.renewStudyAreaFields = [
      {
        fieldGroupClassName: 'p-fluid p-formgrid p-grid',
        fieldGroup: [
          // Left Column Fields
          {
            className: 'p-col-12 p-md-6',
            key: 'studyAreaName',
            type: 'input',
            defaultValue: this.config.data?.studyAreaData?.studyAreaName,
            hide: true,
            templateOptions: {
              label: 'Study Area Name',
              required: false,
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'email',
            type: 'input',
            defaultValue: this.config.data?.studyAreaData.requestor?.email,
            hide: true,
            templateOptions: {
              label: 'Email Address',
              required: false,
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'projectCode',
            type: 'input',
            defaultValue: this.config.data?.studyAreaData?.projectCode,
            templateOptions: {
              label: 'Oracle Project Number',
              required: true,
              placeholder: 'Oracle Project Number',
              minLength: 3,
              maxLength: 255,
              pattern: /^[a-zA-Z0-9 ]+$/,
            },
            validation: {
              messages: {
                required: 'Oracle Project Number is required',
                minlength: 'Minimum 3 characters',
                maxlength: 'Maximum 255 characters',
                pattern: 'Only letters and numbers allowed',
              },
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'taskCode',
            type: 'input',
            defaultValue: this.config.data?.studyAreaData?.taskCode,
            templateOptions: {
              label: 'Oracle Task Number',
              required: true,
              placeholder: 'Oracle Task Number',
              minLength: 3,
              maxLength: 255,
              pattern: /^[a-zA-Z0-9 ]+$/,
            },
            validation: {
              messages: {
                required: 'Oracle Task Number is required',
                minlength: 'Minimum 3 characters',
                maxlength: 'Maximum 255 characters',
                pattern: 'Only letters and numbers allowed',
              },
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'timestamp',
            type: 'datepicker',
            defaultValue: this.getFormattedDate(this.model?.timestamp),
            templateOptions: {
              label: 'Renewal Date',
              required: true,
              disabled: true,
              placeholder: 'Renewal Date',
              maxLength: 255,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 255 characters',
              },
            },
            hooks: {
              onInit: (field) => {
                if (field) {
                  const expiry = new Date(field?.formControl?.value);
                  expiry.setFullYear(expiry.getFullYear() + 1);
                  field?.form?.get('licenseExpiryDate')?.setValue(expiry);
                }
              }
            },
          },
          {
            className: 'p-col-12 p-md-6',
            key: 'licenseExpiryDate',
            type: 'datepicker',
            defaultValue: this.getFormattedDate(this.config.data?.studyAreaData?.licenseExpiryDate),
            templateOptions: {
              label: 'Expiry Date',
              required: true,
              disabled: true,
              placeholder: 'Expiry Date',
              maxLength: 255,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 255 characters',
              },
            },
          },
          {
            className: 'p-col-12 basin-mt-4',
            key: 'content',
            type: 'textarea',
            templateOptions: {
              label: 'Notes',
              rows: 3,
              placeholder: 'Add Note',
              maxLength: 500,
            },
            validation: {
              messages: {
                maxlength: 'Maximum 500 characters',
              },
            },
          },
        ],
      },
    ];
  }

  onSave(): void {
    if (this.form.valid) {
      this.renewStudyArea = {
        email: this.config.data?.studyAreaData.requestor?.email,
        studyAreaName: this.config.data.studyAreaData.studyAreaName,
        projectCode: this.form.value.projectCode,
        taskCode: this.form.value.taskCode,
        invoiceId: this.config.data.studyAreaData.invoiceId,
        comment: this.form.value.content,
      }

      this.studyareaService.saveStudyAreaRenewDetails(this.renewStudyArea)
        .subscribe({
          next: (res) => {
            this.ref.close(this.model);
            this.messageService.add({
              severity: 'success',
              detail: 'Study Area renewed successfully.',
            });
          },
          error: (err: any) => {
            this.messageService.add({
              severity: 'error',
              detail: 'An error occurred while renewing the Study Area. Please try again later.',
            });
          }
        });
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error!',
        detail: 'An error occurred while renewing the Study Area. Please try again later.',
      });
    }
  }

  onCancel(): void {
    this.ref.close();
  }

   getFormattedDate(date: Date | string | null): string {
    return this.appDatePipe.transform(date);
  }
}
